import { useMemo, useRef } from 'react';
import { useLocation, useNavigate, type To } from 'react-router-dom';

/**
 * react-router v7 removed `useHistory`. This keeps the `push`/`replace`/`location` API used
 * across the app on top of `useNavigate`.
 *
 * The returned object is stable like the v5 history was, and reads the current location through a
 * ref. Recreating it per location would re-run every effect that lists `history` as a dependency,
 * which loops when such an effect navigates.
 */
export const useHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;

  return useMemo(
    () => ({
      get location() {
        return locationRef.current;
      },
      push: (to: To, state?: unknown) => navigate(to, { state }),
      replace: (to: To, state?: unknown) => navigate(to, { replace: true, state }),
    }),
    [navigate]
  );
};

/**
 * react-router v7 removed the `useLocation<T>()` generic, `location.state` is now `unknown`.
 */
export const useLocationState = <T>(): Partial<T> => (useLocation().state ?? {}) as Partial<T>;
